import { ZodError, type z } from 'zod';
import { createLogger } from '../logger';

export class SchemaValidationError extends Error {
  readonly issues: ZodError['issues'];

  constructor(message: string, error: ZodError) {
    super(message);
    this.name = 'SchemaValidationError';
    this.issues = error.issues;
  }
}

const DEFAULT_CONTEXT = 'validation';
const validationLogger = createLogger('validation');

function logValidationError(context: string, error: ZodError): void {
  const label = context.trim().length > 0 ? context : DEFAULT_CONTEXT;
  const formatIssue = (issue: ZodError['issues'][number]) => {
    const path = issue.path.length > 0 ? issue.path.join('.') : '<root>';
    return `${path}: ${issue.message}`;
  };
  const headlineCount = 3;
  const headlineIssues = error.issues.slice(0, headlineCount).map((issue) => formatIssue(issue));
  const extraCount = Math.max(0, error.issues.length - headlineCount);
  const summary = headlineIssues.length > 0 ? headlineIssues.join('; ') : 'No issue details provided';
  const suffix = extraCount > 0 ? ` (+${extraCount} more issues)` : '';
  validationLogger.error(`[validation-error] ${label} → ${summary}${suffix}`, {
    context: label,
    issues: error.issues,
  });
}

/**
 * Parse a value with a Zod schema and throw if validation fails.
 */
export function parseOrThrow<TSchema extends z.ZodType>(
  schema: TSchema,
  value: unknown,
  errorMessage = 'Schema validation failed',
  context = DEFAULT_CONTEXT
): z.infer<TSchema> {
  try {
    return schema.parse(value);
  } catch (error) {
    if (error instanceof ZodError) {
      logValidationError(context, error);
      throw new SchemaValidationError(errorMessage, error);
    }
    throw error;
  }
}

/**
 * Parse a value with a Zod schema and return null when validation fails.
 */
export function parseOrNull<TSchema extends z.ZodType>(
  schema: TSchema,
  value: unknown,
  context = DEFAULT_CONTEXT
): z.infer<TSchema> | null {
  const result = schema.safeParse(value);
  if (result.success) {
    return result.data;
  }
  logValidationError(context, result.error);
  return null;
}

/**
 * Parse a value with a Zod schema and return the supplied fallback when validation fails.
 */
export function parseOrDefault<TSchema extends z.ZodType, TFallback>(
  schema: TSchema,
  value: unknown,
  fallback: TFallback,
  context = DEFAULT_CONTEXT
): z.infer<TSchema> | TFallback {
  const result = schema.safeParse(value);
  if (result.success) {
    return result.data;
  }
  logValidationError(context, result.error);
  return fallback;
}
